import type { ViewsService } from '@/engine/app/Views.service';
import type { ObjectBoxInfo, TuneControlInfo, GroupInfo } from '@/engine/app/models/viewEntity/view.entity';
import { useInjection } from 'inversify-react';
import type { MeasurementBoxesController } from '../boxes/measurementBoxes';
import type { MeasurementBoxProps } from '../boxes/measurementBoxes/MeasurementBoxesController/MeasurementBoxes.controller';
import { TYPES } from '@/engine/ioc';
import type { FilledGroupInfo, PlanningEntity } from '@/components/contentContainers/PlanningContainer.tsx';

export const usePlanningEntities = (): Array<PlanningEntity | FilledGroupInfo> => {
  const { currentView }: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);

  if (!currentView) {
    throw new Error('View should be defined by current step');
  }

  const measurementController: MeasurementBoxesController = useInjection<MeasurementBoxesController>(
    TYPES.IMeasurementsBoxesController
  );

  const objectBoxes: Array<ObjectBoxInfo> = [...currentView.getObjectBoxes()];
  const measurementBoxes: Array<MeasurementBoxProps> = [...measurementController.getMeasurementBoxes()];
  const tuneControls: Array<TuneControlInfo> = [...currentView.getTuneControls()];

  const entitiesGroups: Array<GroupInfo> = [...currentView.getPlanningEntitiesGroups()];

  const filledEntitiesGroups: Array<GroupInfo & { entities: Array<PlanningEntity> }> = entitiesGroups.map((group) => {
    const entities: Array<PlanningEntity> = [];

    // Extract matching items from objectBoxes
    const matchedObjectBoxes: Array<ObjectBoxInfo> = [];
    objectBoxes.forEach((box) => {
      if (box.group === group.descriptor) {
        entities.push(box);
        matchedObjectBoxes.push(box);
      }
    });
    // Remove matched controls from measurementBoxes
    matchedObjectBoxes.forEach((box) => {
      const index = objectBoxes.indexOf(box);
      if (index !== -1) {
        objectBoxes.splice(index, 1);
      }
    });

    // Extract matching items from measurementBoxes
    const matchedMeasurementBoxes: Array<MeasurementBoxProps> = [];
    measurementBoxes.forEach((box) => {
      if (box.group === group.descriptor) {
        entities.push(box);
        matchedMeasurementBoxes.push(box);
      }
    });
    // Remove matched controls from measurementBoxes
    matchedMeasurementBoxes.forEach((box) => {
      const index = measurementBoxes.indexOf(box);
      if (index !== -1) {
        measurementBoxes.splice(index, 1);
      }
    });

    // Extract matching items from tuneControls
    const matchedControls: Array<TuneControlInfo> = [];
    tuneControls.forEach((control) => {
      if (control.group === group.descriptor) {
        entities.push(control);
        matchedControls.push(control);
      }
    });
    // Remove matched controls from tuneControls
    matchedControls.forEach((control) => {
      const index = tuneControls.indexOf(control);
      if (index !== -1) {
        tuneControls.splice(index, 1);
      }
    });

    return {
      ...group,
      entities: entities.sort((a, b) => a.order - b.order)
    };
  });

  const combinedEntities = [...tuneControls, ...measurementBoxes, ...objectBoxes, ...filledEntitiesGroups];

  const sortedEntities = combinedEntities.sort((a, b) => a.order - b.order);

  return sortedEntities.map((entity, index) => ({
    ...entity,
    order: index
  }));
};
